import { useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  IInboxActionResult,
  IInboxApiAttachment,
  IInboxCancelRequest,
  IInboxConversationRow,
  IInboxOrderRefundRequest,
  IInboxReplyRequest,
  IInboxReturnRefundRequest,
  IInboxThreadMessage,
  IInboxTypeRequest,
} from "../models/messagesInbox/IInboxApi";
import { InboxType } from "../models/messagesInbox/IInboxConversation";
import { DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";
import { conversationPath, messagesInboxKeys } from "./useMessagesInbox";

/** What happened. blocked = sending is switched off on this Wilma, so nothing reached the buyer or eBay. */
export interface IInboxActionOutcome extends IInboxActionResult {
  message: string;
}

/**
 * The API answers 200 with success false and data.blocked when sending is switched off here; that
 * isn't an error, nothing was done. Any other refusal (eBay said no, bad request) is thrown so the
 * caller shows the API's message.
 */
async function outcome(request: Promise<DefaultResponseWithData<IInboxActionResult | null>>): Promise<IInboxActionOutcome> {
  const res = await request;
  if (res.data?.blocked) return { ...res.data, blocked: true, message: res.message };
  if (!res.success) throw new Error(res.message || "Something went wrong – nothing was done.");
  return { ...res.data, blocked: false, message: res.message };
}

interface IReplyVariables {
  conversationId: string;
  body: string;
  /** Already uploaded (uploadAttachments); the reply names them. */
  attachments?: IInboxApiAttachment[];
}

/** A plain API answer: success false is a refusal with the API's message. */
async function unwrap<T>(request: Promise<DefaultResponseWithData<T>>): Promise<T> {
  const res = await request;
  if (!res.success) throw new Error(res.message || "Something went wrong – nothing was done.");
  return res.data;
}

interface IOrderVariables {
  partId: string;
  conversationId: string;
}

export default function useMessagesInboxMutations(componentName = "useMessagesInboxMutations") {
  const { post, put, del, postDocs } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  const refreshAfter = (conversationId: string, partId?: string) => {
    queryClient.invalidateQueries(messagesInboxKeys.thread(conversationId));
    queryClient.invalidateQueries(messagesInboxKeys.conversations(true));
    if (partId) queryClient.invalidateQueries(messagesInboxKeys.partContextAll(partId));
  };

  const reply = useMutation(
    ({ conversationId, body, attachments }: IReplyVariables) =>
      outcome(
        post<IInboxReplyRequest, DefaultResponseWithData<IInboxActionResult>>(`${conversationPath(conversationId)}/reply`, {
          body,
          ...(attachments && attachments.length > 0 ? { attachments } : {}),
        })
      ),
    {
      onSuccess: (data, { conversationId }) => {
        if (!data.blocked) refreshAfter(conversationId);
      },
    }
  );

  /**
   * Photos for a reply go up first (multipart "files"), and the API hands back where they live;
   * the reply then names them. Nothing reaches the buyer until the reply itself is sent.
   */
  const uploadAttachments = useMutation(({ conversationId, files }: { conversationId: string; files: File[] }) => {
    const form = new FormData();
    files.forEach((f) => form.append("files", f, f.name));
    return unwrap(postDocs<DefaultResponseWithData<IInboxApiAttachment[]>>(`${conversationPath(conversationId)}/attachments`, form));
  });

  /** "No reply needed" on (POST) or off (DELETE). The row as it now stands comes back. */
  const setNoReplyNeeded = useMutation(
    ({ conversationId, needed }: { conversationId: string; needed: boolean }) =>
      unwrap(
        needed
          ? post<Record<string, never>, DefaultResponseWithData<IInboxConversationRow>>(`${conversationPath(conversationId)}/no-reply-needed`, {})
          : del<DefaultResponseWithData<IInboxConversationRow>>(`${conversationPath(conversationId)}/no-reply-needed`)
      ),
    { onSuccess: () => queryClient.invalidateQueries(messagesInboxKeys.conversations(true)) }
  );

  /** Corrects what the buyer wants when AI read it wrong. The row as it now stands comes back. */
  const setType = useMutation(
    ({ conversationId, type }: { conversationId: string; type: InboxType }) =>
      unwrap(put<IInboxTypeRequest, DefaultResponseWithData<IInboxConversationRow>>(`${conversationPath(conversationId)}/type`, { type })),
    { onSuccess: () => queryClient.invalidateQueries(messagesInboxKeys.conversations(true)) }
  );

  /** An internal note: stored in Wilma, shown in the thread, never sent to the buyer. */
  const addNote = useMutation(
    ({ conversationId, body }: { conversationId: string; body: string }) =>
      unwrap(post<{ body: string }, DefaultResponseWithData<IInboxThreadMessage>>(`${conversationPath(conversationId)}/notes`, { body })),
    { onSuccess: (_, { conversationId }) => refreshAfter(conversationId) }
  );

  const deleteNote = useMutation(
    ({ conversationId, noteId }: { conversationId: string; noteId: string }) =>
      unwrap(del<DefaultResponseWithData<boolean>>(`${conversationPath(conversationId)}/notes/${encodeURIComponent(noteId)}`)),
    { onSuccess: (_, { conversationId }) => refreshAfter(conversationId) }
  );

  const cancelOrder = useMutation(
    ({ partId, ...body }: IOrderVariables & IInboxCancelRequest) =>
      outcome(post<IInboxCancelRequest, DefaultResponseWithData<IInboxActionResult>>(`/messagesinbox/orders/${encodeURIComponent(partId)}/cancel`, { reason: body.reason })),
    {
      onSuccess: (data, { conversationId, partId }) => {
        if (!data.blocked) refreshAfter(conversationId, partId);
      },
    }
  );

  const refundOrder = useMutation(
    ({ partId, conversationId, ...body }: IOrderVariables & IInboxOrderRefundRequest) =>
      outcome(post<IInboxOrderRefundRequest, DefaultResponseWithData<IInboxActionResult>>(`/messagesinbox/orders/${encodeURIComponent(partId)}/refund`, body)),
    {
      onSuccess: (data, { conversationId, partId }) => {
        if (!data.blocked) refreshAfter(conversationId, partId);
      },
    }
  );

  const refundReturn = useMutation(
    ({ returnId, partId, conversationId, ...body }: IOrderVariables & IInboxReturnRefundRequest & { returnId: string }) =>
      outcome(post<IInboxReturnRefundRequest, DefaultResponseWithData<IInboxActionResult>>(`/messagesinbox/returns/${encodeURIComponent(returnId)}/refund`, body)),
    {
      onSuccess: (data, { conversationId, partId }) => {
        if (!data.blocked) refreshAfter(conversationId, partId);
      },
    }
  );

  return { reply, uploadAttachments, setNoReplyNeeded, setType, addNote, deleteNote, cancelOrder, refundOrder, refundReturn };
}
